/**
 * The command in hand (D219, widened by D226 and D229): what the sentence
 * being answered named, for the three conditions that read the sentence
 * rather than the world.
 *
 * @remarks
 * Conditions are evaluated against the world: the bundle and the runtime
 * state. D219's `indirect_object_is` asks a different question, one no state
 * can answer: WHO was this offered to? D226's `direct_object_is` (E19) asks
 * its twin: WHAT was this about? The answers live only in the parsed
 * command, and only while that command is being answered. So the command is
 * handed to the evaluator alongside the world, by the callers that have one:
 * the refusal a thing speaks for a verb it does not do
 * (`unsupportedVerbNarratives`), and — since D226 — the occurrence a command
 * raises (`command_executed`, `command_rejected`), which the triggers
 * listening for it read.
 *
 * Everywhere else there is no command in hand, the parameter is left out, and
 * both conditions are false. The loader rejects them anywhere a command is
 * not in hand, so an author cannot write a variant that could never be
 * chosen.
 *
 * A room or the hands named in a slot is not an item (D41, D63): the slot
 * reads as naming nothing, and a condition with no `itemId` — "any direct
 * object" — stays false for `examine the room`. The conditions ask after
 * THINGS.
 *
 * @packageDocumentation
 */

import type { ItemId } from '@orpheus/content-schema';
import type { EntityRef, ParsedCommand } from '@orpheus/parser-engine';

import { actorIdForEntityRef, isBodyRef, itemIdForEntityRef, roomIdForEntityRef } from '../entities/entity-ref.js';

/** What the command being answered named, as a condition may ask after it. */
export interface CommandInHand {
	/** The direct object — what `examine X` is about, what `take X` takes, the X of `give X to Y` — when one was named and is a thing. */
	readonly directObjectItemId: ItemId | undefined;
	/** The second object — the recipient of `give X to Y`, the audience of `show X to Y` — when one was named and is a thing. */
	readonly indirectObjectItemId: ItemId | undefined;
	/** D229: the tool — the Y of `cut X with Y`, `open X using Y` — when one was named and is a thing (the hands are not). */
	readonly toolItemId: ItemId | undefined;
}

/**
 * Read what a parsed command named into the shape a condition asks after.
 *
 * @param command - The parsed command being answered
 * @returns The command in hand
 */
export const commandInHandOf = (command: ParsedCommand): CommandInHand => ({
	directObjectItemId: itemIdNamedBy(command.directObjectRef),
	indirectObjectItemId: itemIdNamedBy(command.indirectObjectRef),
	toolItemId: itemIdNamedBy(command.toolRef),
});

/**
 * The item a slot names, when the slot holds a THING — not the room, not the
 * hands, not nothing.
 *
 * @param entityRef - The slot's resolved reference, if any
 * @returns The item id, or `undefined`
 */
const itemIdNamedBy = (entityRef: EntityRef | undefined): ItemId | undefined =>
	// D232: nor an actor — a body in a slot names no item (a sentence condition about actors waits for a caller).
	entityRef === undefined || isBodyRef(entityRef) || roomIdForEntityRef(entityRef) !== undefined || actorIdForEntityRef(entityRef) !== undefined
		? undefined
		: itemIdForEntityRef(entityRef);
